import React, { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const StudentProfile = () => {
  const { getFirestore } = useFirebase();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    postcode: "",
    gearbox: "",
    licenseNumber: "",
    emergencyContact: "",
    emergencyPhone: "",
    medicalConditions: false,
    medicalDetails: "",
    nervousAboutDriving: false,
    specificGoals: "",
    focusAreas: "",
    drivingExperience: "",
    vehicleType: "",
    licenseType: "",
    previousLessons: "0"
  });

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const db = getFirestore();
          const userRef = doc(db, "users", user.id);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setFormData({
              firstName: userData.firstName || "",
              lastName: userData.lastName || "",
              email: userData.email || "",
              phone: userData.phone || "",
              address: userData.address || "",
              postcode: userData.postcode || "",
              gearbox: userData.gearbox || "",
              licenseNumber: userData.licenseNumber || "",
              emergencyContact: userData.emergencyContact || "",
              emergencyPhone: userData.emergencyPhone || "",
              medicalConditions: userData.medicalConditions || false,
              medicalDetails: userData.medicalDetails || "",
              nervousAboutDriving: userData.nervousAboutDriving || false,
              specificGoals: userData.specificGoals || "",
              focusAreas: userData.focusAreas || "",
              drivingExperience: userData.drivingExperience || "",
              vehicleType: userData.vehicleType || "",
              licenseType: userData.licenseType || "",
              previousLessons: userData.previousLessons || "0"
            });
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          toast({
            title: "Error",
            description: "Failed to load profile data",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [user, getFirestore, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const db = getFirestore();
      if (!user?.id) return;

      await updateDoc(doc(db, "users", user.id), {
        ...formData,
        updatedAt: new Date().toISOString(),
        needsUpdate: false
      });

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Profile</h1>
            <p className="text-gray-500 mt-2">Loading profile information...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Profile</h1>
          <p className="text-gray-500 mt-2">Manage your profile information</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Avatar className="h-24 w-24">
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(`${formData.firstName} ${formData.lastName}`)}&background=random`}
                  alt="Profile"
                />
              </Avatar>
              <div>
                <CardTitle>{`${formData.firstName} ${formData.lastName}`}</CardTitle>
                <p className="text-gray-500">Student</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Contact Information</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input
                      id="postcode"
                      value={formData.postcode}
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergencyContact">Emergency Contact</Label>
                    <Input
                      id="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergencyPhone">Emergency Phone</Label>
                    <Input
                      id="emergencyPhone"
                      value={formData.emergencyPhone}
                      onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Driving Information</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">License Number</Label>
                    <Input
                      id="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gearbox">Preferred Gearbox Type</Label>
                    <select
                      id="gearbox"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={formData.gearbox}
                      onChange={(e) => setFormData({ ...formData, gearbox: e.target.value })}
                    >
                      <option value="">Select gearbox type</option>
                      <option value="manual">Manual</option>
                      <option value="automatic">Automatic</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="drivingExperience">Driving Experience</Label>
                    <Input
                      id="drivingExperience"
                      value={formData.drivingExperience}
                      onChange={(e) => setFormData({ ...formData, drivingExperience: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicleType">Vehicle Type</Label>
                    <Input
                      id="vehicleType"
                      value={formData.vehicleType}
                      onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="licenseType">License Type</Label>
                    <Input
                      id="licenseType"
                      value={formData.licenseType}
                      onChange={(e) => setFormData({ ...formData, licenseType: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="previousLessons">Previous Lessons</Label>
                    <Input
                      id="previousLessons"
                      type="number"
                      value={formData.previousLessons}
                      onChange={(e) => setFormData({ ...formData, previousLessons: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Learning Goals</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="specificGoals">Specific Goals</Label>
                    <Textarea
                      id="specificGoals"
                      value={formData.specificGoals}
                      onChange={(e) => setFormData({ ...formData, specificGoals: e.target.value })}
                      placeholder="What are your specific goals for learning to drive?"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="focusAreas">Focus Areas</Label>
                    <Textarea
                      id="focusAreas"
                      value={formData.focusAreas}
                      onChange={(e) => setFormData({ ...formData, focusAreas: e.target.value })}
                      placeholder="Areas you'd like to focus on during lessons"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Health & Safety</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="medicalConditions"
                        checked={formData.medicalConditions}
                        onCheckedChange={(checked) => setFormData({ ...formData, medicalConditions: checked })}
                      />
                      <Label htmlFor="medicalConditions">Medical Conditions</Label>
                    </div>
                    {formData.medicalConditions && (
                      <Textarea
                        id="medicalDetails"
                        value={formData.medicalDetails}
                        onChange={(e) => setFormData({ ...formData, medicalDetails: e.target.value })}
                        placeholder="Please provide details about your medical conditions"
                        className="mt-2"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="nervousAboutDriving"
                        checked={formData.nervousAboutDriving}
                        onCheckedChange={(checked) => setFormData({ ...formData, nervousAboutDriving: checked })}
                      />
                      <Label htmlFor="nervousAboutDriving">Nervous About Driving</Label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudentProfile; 